import { useRef, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, TrendingUp, Gift } from 'lucide-react';

const steps = [
  {
    icon: Sparkles,
    num: '01',
    title: 'Earn',
    subtitle: 'Glow Points',
    description: 'Earn Glow Points on every treatment, referral, review, and more. Your loyalty literally pays off — with every rupee spent bringing you closer to extraordinary rewards.',
    highlight: '1–5 GP per ₹100',
    accent: 'hsl(40 76% 44%)',
  },
  {
    icon: TrendingUp,
    num: '02',
    title: 'Grow',
    subtitle: 'Your Tier',
    description: 'As you spend, you rise through 5 exclusive tiers — Bloom, Lustre, Aura, Luminary, and Leor Glow. Each tier unlocks deeper discounts, priority booking, and premium perks.',
    highlight: '5 Exclusive Tiers',
    accent: 'hsl(42 70% 58%)',
  },
  {
    icon: Gift,
    num: '03',
    title: 'Redeem',
    subtitle: 'Luxury Treatments',
    description: 'Use your Glow Points for complimentary facials, body treatments, fillers, and premium services. Turn your devotion to self-care into tangible luxury.',
    highlight: '1,000 GP = ₹500',
    accent: 'hsl(195 50% 82%)',
  },
];

const VIDEO_URL = 'https://customer-assets.emergentagent.com/job_leor-vr-rewards/artifacts/ax1b181m_shutterstock_3801911303-2.mov';

/* Clamp utility */
function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}

/* Map value from one range to another, clamped */
function mapRange(value, inMin, inMax, outMin, outMax) {
  const t = clamp((value - inMin) / (inMax - inMin), 0, 1);
  return outMin + t * (outMax - outMin);
}

export const ProgramOverview = () => {
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0);

  /* Manual scroll progress calculation — reliable with sticky */
  const onScroll = useCallback(() => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const sectionHeight = sectionRef.current.offsetHeight;
    const viewportH = window.innerHeight;
    // progress 0 when section top hits viewport top, 1 when section bottom hits viewport bottom
    const scrollRange = sectionHeight - viewportH;
    const scrolled = -rect.top; // how far past section top
    const p = clamp(scrolled / scrollRange, 0, 1);
    setProgress(p);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // init
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  /*
   * Timeline (progress 0 → 1):
   * 0.00 → 0.05:  Header fades in
   * 0.05 → 0.15:  Header holds
   * 0.15 → 0.20:  Header fades out
   * 0.20 → 0.25:  Step 1 fades in
   * 0.25 → 0.37:  Step 1 holds
   * 0.37 → 0.42:  Step 1 fades out
   * 0.42 → 0.47:  Step 2 fades in
   * 0.47 → 0.60:  Step 2 holds
   * 0.60 → 0.65:  Step 2 fades out
   * 0.65 → 0.70:  Step 3 fades in
   * 0.70 → 0.90:  Step 3 holds
   * 0.90 → 0.95:  Step 3 fades out
   */

  // Header
  let headerOpacity = 0;
  let headerY = 50;
  if (progress < 0.05) {
    headerOpacity = mapRange(progress, 0, 0.05, 0, 1);
    headerY = mapRange(progress, 0, 0.05, 50, 0);
  } else if (progress < 0.15) {
    headerOpacity = 1;
    headerY = 0;
  } else if (progress < 0.20) {
    headerOpacity = mapRange(progress, 0.15, 0.20, 1, 0);
    headerY = mapRange(progress, 0.15, 0.20, 0, -40);
  }

  // Steps
  const stepRanges = [
    { fadeIn: [0.20, 0.25], hold: [0.25, 0.37], fadeOut: [0.37, 0.42] },
    { fadeIn: [0.42, 0.47], hold: [0.47, 0.60], fadeOut: [0.60, 0.65] },
    { fadeIn: [0.65, 0.70], hold: [0.70, 0.90], fadeOut: [0.90, 0.95] },
  ];

  const stepAnims = stepRanges.map((range) => {
    let opacity = 0;
    let y = 50;
    if (progress >= range.fadeIn[0] && progress < range.fadeIn[1]) {
      opacity = mapRange(progress, range.fadeIn[0], range.fadeIn[1], 0, 1);
      y = mapRange(progress, range.fadeIn[0], range.fadeIn[1], 50, 0);
    } else if (progress >= range.hold[0] && progress < range.hold[1]) {
      opacity = 1;
      y = 0;
    } else if (progress >= range.fadeOut[0] && progress < range.fadeOut[1]) {
      opacity = mapRange(progress, range.fadeOut[0], range.fadeOut[1], 1, 0);
      y = mapRange(progress, range.fadeOut[0], range.fadeOut[1], 0, -40);
    }
    return { opacity, y };
  });

  // Progress bar width
  const barWidth = `${clamp(mapRange(progress, 0.05, 0.92, 0, 100), 0, 100)}%`;

  return (
    <section id="program" ref={sectionRef} className="relative" style={{ height: '400vh' }}>
      {/* Sticky viewport-filling container */}
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.35) saturate(1.2)' }}
          >
            <source src={VIDEO_URL} type="video/mp4" />
            <source src={VIDEO_URL} type="video/quicktime" />
          </video>
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, hsl(20 18% 7% / 0.6) 0%, hsl(20 18% 7% / 0.3) 50%, hsl(20 18% 7% / 0.6) 100%)',
            }}
          />
        </div>

        {/* HEADER */}
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-6 pointer-events-none"
          style={{ opacity: headerOpacity, transform: `translateY(${headerY}px)` }}
        >
          <div className="flex items-center gap-3 justify-center mb-6">
            <div className="h-px w-12 bg-gold/30" />
            <svg width="14" height="14" viewBox="0 0 20 20" className="text-gold/50">
              <path d="M10 0L12 8L20 10L12 12L10 20L8 12L0 10L8 8Z" fill="currentColor" />
            </svg>
            <div className="h-px w-12 bg-gold/30" />
          </div>
          <span className="font-body text-xs tracking-[0.3em] uppercase text-gold mb-5 block">
            How It Works
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground font-medium leading-tight">
            Your Path to <span className="text-shimmer-gold italic">Radiance</span>
          </h2>
          <p className="font-body text-base text-cream-soft/70 mt-5 max-w-md leading-relaxed">
            Three simple steps to transform every visit into lasting rewards and exclusive luxury.
          </p>
        </div>

        {/* STEP CARDS */}
        {steps.map((step, i) => (
          <div
            key={step.title}
            className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-6 pointer-events-none"
            style={{
              opacity: stepAnims[i].opacity,
              transform: `translateY(${stepAnims[i].y}px)`,
            }}
          >
            <span
              className="font-display text-7xl lg:text-8xl font-medium mb-4 block"
              style={{ color: step.accent, opacity: 0.12 }}
            >
              {step.num}
            </span>

            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
              style={{ background: `${step.accent}18`, border: `1px solid ${step.accent}28` }}
            >
              <step.icon className="w-6 h-6" style={{ color: step.accent }} />
            </div>

            <h3 className="font-display text-4xl lg:text-5xl text-foreground mb-1">{step.title}</h3>
            <p className="font-body text-sm mb-5" style={{ color: step.accent }}>
              {step.subtitle}
            </p>

            <p className="font-body text-base text-cream-soft/75 max-w-lg leading-relaxed mb-7">
              {step.description}
            </p>

            <div
              className="inline-flex items-center px-5 py-2.5 rounded-full"
              style={{ background: `${step.accent}12`, border: `1px solid ${step.accent}22` }}
            >
              <span className="font-body text-sm tracking-wide" style={{ color: step.accent }}>
                {step.highlight}
              </span>
            </div>
          </div>
        ))}

        {/* Progress bar */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 w-52">
          <div className="h-0.5 bg-foreground/10 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-gold"
              style={{ width: barWidth, transition: 'width 0.1s linear' }}
            />
          </div>
          <div className="flex justify-between mt-2.5">
            {steps.map((s) => (
              <span key={s.title} className="font-body text-[10px] text-muted-foreground uppercase tracking-wider">
                {s.title}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom ornament */}
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 w-full max-w-xs">
          <div className="flex items-center gap-3 justify-center">
            <div className="h-px flex-1 bg-gold/15" />
            <svg width="10" height="10" viewBox="0 0 20 20" className="text-gold/25">
              <path d="M10 0L12 8L20 10L12 12L10 20L8 12L0 10L8 8Z" fill="currentColor" />
            </svg>
            <div className="h-px flex-1 bg-gold/15" />
          </div>
        </div>
      </div>
    </section>
  );
};
